import { useStyles, Button, MessageTooltip } from '@archipro-design/aria';
import { PencilLine } from '@archipro-design/icons';
import * as S from './DetailPageButtons.style';
import React from 'react';
import { useEditModeNavigation } from '~/modules/root/hook/use-edit-mode-navigation';

interface EditModeToggleProps {
    editLink: string;
}

export const EditModeToggle = ({ editLink }: EditModeToggleProps) => {
    const { css } = useStyles();
    const { isNavigating, start } = useEditModeNavigation(editLink);

    const triggerRef = React.useRef<HTMLDivElement>(null);
    const [tooltipOpen, setTooltipOpen] = React.useState(false);

    const showTooltip = () => {
        if (isNavigating) return;
        setTooltipOpen(true);
    };

    const hideTooltip = () => {
        setTooltipOpen(false);
    };

    return (
        <MessageTooltip
            open={tooltipOpen}
            trigger={
                <div
                    ref={triggerRef}
                    onMouseEnter={showTooltip}
                    onMouseLeave={hideTooltip}
                    onMouseDown={hideTooltip}
                >
                    <Button
                        id={'BMEditModeToggle'}
                        className={
                            isNavigating
                                ? css(S.ActionButton, S.ActionButtonNavigating)
                                : css(S.ActionButton)
                        }
                        onClick={start}
                        disabled={isNavigating}
                        loading={isNavigating}
                        aria-busy={isNavigating || undefined}
                        aria-label={
                            isNavigating
                                ? 'Switching to edit mode'
                                : 'Switch to edit mode'
                        }
                        // Keep the pencil mounted so size={30} still sets the button footprint.
                        // Aria's loader is absolute and does not reserve layout space on its own.
                        icon={
                            <PencilLine
                                aria-hidden
                                className={
                                    isNavigating
                                        ? css(S.ActionButtonHiddenIcon)
                                        : undefined
                                }
                            />
                        }
                        size={30}
                        color="white"
                        variables={{
                            iconPadding: 0,
                            buttonPadding: 16,
                            buttonRadius: `50%`,
                        }}
                    />
                </div>
            }
            content={{
                children: 'Switch to edit mode',
                className: css(S.TooltipContent),
                size: 18,
                padding: 12,
                color: 'white',
            }}
            position="after"
            offset={[1, 9]}
        />
    );
};
